<template>
  <page-container
    :hide-footer="true"
    :narrow="false"
    :show-appointment-info="false"
    :hide-back-button="true"
    :large-header="!showFilterRow"
    :title="isLoading? '' : calendarTitle"
  >
    <template
      v-if="showHeaderContent"
      #headerContent
    >
      <filters-row
        :show-filter-row="showFilterRow"
        :require-search-before-time-showing="requireSearchBeforeTimeShowing"
        :filters-info="filtersInfo"
        :all-showed-filters-filled="allShowedFiltersFilled"
        @filterChanged="filterChanged"
        @trackFilterByName="trackFilterByName"
        @searchCalendars="searchCalendars"
      />
      <pp-container
        v-if="showTimeSelectionBlocks"
        class="calendar-nav my-0"
        :class="[
          isMobile ? 'pa-0 mb-4' : 'pt-2 px-5 pb-0 mb-3',
          ...calendarNavClasses
        ]"
      >
        <pp-row
          class="my-0 px-3"
        >
          <pp-col
            cols="5"
            if="!isMobile"
            class="d-none d-sm-flex text-h4 grey--text text--darken-4"
          >
            <pp-input-toggle-group
              v-if="showVisitTypeFilterNearCalendar"
              v-model="filters.visitTypeFilter.value"
              :options="filters.visitTypeFilter.expand.options"
              data-cy-id="visit-type-options-toggle"
              mandatory
              class="col-12 mt-auto"
              @blur="filterChanged('visitTypeFilter', $event)"
            />
            <template v-else>
              Choose a time
            </template>
          </pp-col>
          <pp-col
            cols="12"
            sm="7"
            class="calendar-nav__row__col pa-0 align-center d-flex"
          >
            <pp-calendar-nav
              :days-to-navigate="daysToNavigate"
              :days-to-show="daysToShow"
              :disable-back-button="disableBackButton"
              :disable-forward-button="disableForwardButton"
              :start-date="startDate"
              class="px-3"
              :class="[
                isMobile ? '' : 'py-2'
              ]"
              @update="updateCalendarNav"
            />
          </pp-col>
        </pp-row>
      </pp-container>
    </template>
    <template
      #content
    >
      <pp-loader v-if="isLoading" />
      <template v-else-if="showCalendars">
        <template v-if="showTimeSelectionBlocks">
          <ul>
            <li
              v-for="(calendar, calendarIndex) in calendarsToShow"
              :key="calendarIndex"
              class="row calendar-row px-5"
              :calendar_index="calendarIndex"
            >
              <pp-col
                cols="12"
                sm="5"
                class="py-0 py-sm-3 pl-2"
              >
                <pp-avatar-info
                  :address="calendar.displayInfo.address"
                  :image="calendar.displayInfo.image"
                  :name="calendar.displayInfo.name"
                  :specialty="calendar.displayInfo.specialty"
                  size="xxl"
                  :virtual-visit="isVirtualVisitFilterEnabled"
                  class="py-0 py-sm-3"
                />
              </pp-col>
              <pp-col
                cols="12"
                sm="7"
              >
                <pp-row
                  class="text-center no-gutters availability-container"
                >
                  <pp-col
                    v-if="!isMobile"
                    cols="1"
                  />
                  <pp-col
                    v-if="showCallOfficeBlock(calendarIndex)"
                    class="text-center ma-auto"
                  >
                    <call-office
                      :appt-phone="calendar.displayInfo.phone"
                      :appt-formatted-phone="calendar.displayInfo.phoneFormatted"
                      :name="calendar.displayInfo.name"
                      :type="calendar.type"
                    />
                  </pp-col>
                  <template
                    v-else
                  >
                    <div
                      v-if="showNextAvailableButton(calendarIndex)"
                      class="d-flex flex-grow-1 align-center"
                      :class="[
                        isMobile ? 'mx-n5 mt-3' : ''
                      ]"
                    >
                      <pp-button
                        class="ma-auto"
                        :class="[
                          isMobile ? 'flex-grow-1' : ''
                        ]"
                        :type="isMobile ? 'primary' : 'hover'"
                        data-cy-id="next-available-btn"
                        @click="navigateToNextAvailable(nearestAvailabilityInFuture(calendarIndex).dateForNavigation)"
                      >
                        <template #text>
                          <span class="text-body-2">Next available: <b>{{ nearestAvailabilityInFuture(calendarIndex).text }}</b></span>
                        </template>
                      </pp-button>
                    </div>
                    <template
                      v-for="(daySlot, dayIndex) in daySlots"
                      v-else
                    >
                      <pp-col
                        :key="dayIndex"
                        class="text-center"
                      >
                        <div class="time-blocks mt-n1">
                          <pp-scrollable-list
                            v-if="filteredBlocks[calendarIndex].timeBlocks[getDateByDaysFromStartDate(dayIndex)]"
                            :default-visible-items="3"
                            :horizontal="scrollableHorizontal"
                            :items="filteredBlocks[calendarIndex].timeBlocks[getDateByDaysFromStartDate(dayIndex)]"
                            :show-all="showMore(calendarIndex)"
                            @more-click="clickMore(calendarIndex)"
                          >
                            <template
                              v-for="(timeSlot, index) in filteredBlocks[calendarIndex].timeBlocks[getDateByDaysFromStartDate(dayIndex)]"
                              v-slot:[`scrollable-list-item-${index}`]="{ item }"
                            >
                              <pp-button
                                :key="timeSlot.formattedStartTime"
                                :class="item.state === 'avail' ? 'time-blocks__slot--available' : 'time-blocks__slot--booked'"
                                :disabled="item.state !== 'avail'"
                                :text="item.formattedStartTime"
                                class="time-blocks__slot"
                                type="hover"
                                @click="goToNextPage(timeSlot, calendarIndex)"
                              />
                            </template>
                            <template #more-button>
                              <pp-button
                                class="more-button my-1"
                                text="more"
                                type="hover"
                                @click="clickMore(calendarIndex)"
                              />
                            </template>
                          </pp-scrollable-list>
                          <template v-else>
                            <span class="d-sm-none"><i>No Times Available</i></span>
                          </template>
                        </div>
                      </pp-col>
                    </template>
                  </template>
                  <pp-col
                    v-if="!isMobile"
                    cols="1"
                  />
                </pp-row>
              </pp-col>
            </li>
          </ul>
        </template>
        <make-selection
          v-else
          class="ma-auto"
        />
      </template>
      <div
        v-else-if="showLoadingError"
        class="loading-error mt-5 px-5"
      >
        <p
          id="no-availability"
          aria-labelledby="calendar-title no-availability"
          class="mb-5"
        >
          There are currently no available appointments with online booking.
        </p>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p v-html="errorMessage" />
      </div>
    </template>
  </page-container>
</template>

<script>
import PageContainer from '@/components/page-container/PageContainer'
import CallOffice from '@/components/call-office/CallOffice'
import FiltersRow from '@/components/filters-row/FiltersRow'
import formAnalytics from '@/mixins/formAnalytics'
import Vuex from 'vuex'
import MakeSelection from '@/components/make-selection/MakeSelection'
import {
  TWO_WAY_EXTERNAL_CALENDAR_SOURCES,
  VISIT_TYPE_IN_PERSON_VALUE,
  VISIT_TYPE_VIRTUAL_VISIT_VALUE,
  REFERRER_BACK_BUTTON,
  REFERRER_CLOSE_BUTTON,
  NO_INSURANCE_OPTION_ID,
  OTHER_INSURANCE_OPTION_ID,
  CALENDAR_GROUPS,
  CALENDAR_TYPES,
  AVAILABILITY_TYPE_IN_OFFICE_ONLY,
  AVAILABILITY_TYPE_VIRTUAL_ONLY,
  AVAILABILITY_TYPE_IN_OFFICE_OR_VIRTUAL,
  NOCALENDAR_STATUS,
  FILTER_VALUE_TO_SCREENING_VALUE_MAP
} from '@/views/constants'
import { validAppointmentTypeByPatientType, validLegacyReasonType } from '@/utils/helpers'

import * as types from '@/store/legacy/mutation-types'
import * as uiTypes from '@/store/ui/mutations-types'
import { ScreeningSettings } from '@/utils/screeningSettings'
import { datadogRum } from '@datadog/browser-rum'

const { mapState, mapGetters, mapMutations, mapActions } = Vuex

const screeningQuestions = new ScreeningSettings()

export default {
  name: 'ViewsStepOne',
  components: {
    'page-container': PageContainer,
    'call-office': CallOffice,
    'make-selection': MakeSelection,
    'filters-row': FiltersRow
  },
  mixins: [formAnalytics],
  data () {
    const curDate = new Date(Date.now())
    return {
      minDate: new Date(curDate),
      startDate: new Date(curDate),
      showAllTimes: [],
      isLoading: true,
      isMobile: this.$ppUi.isMobile,
      errorMessage: '',
      showFilteredResults: false,
      showFilterErrors: false
    }
  },
  computed: {
    ...mapState('legacy', ['calendars', 'reasons', 'insurances', 'appointmentTypesEnabled', 'calendarIndexToLocationAndProviderMap']),
    ...mapGetters({
      formData: 'formData/formData',
      filterIsFilled: 'ui/filterIsFilled',
      fieldSettings: 'legacy/fieldSettings',
      calendarTitle: 'legacy/calendarTitle',
      screeningSettings: 'legacy/screeningSettings',
      getPracticeGroup: 'legacy/getPracticeGroup',
      configuredAppointmentTypesByScheduleIdsList: 'legacy/configuredAppointmentTypesByScheduleIdsList',
      allAppointmentTypesWithActiveConfigurations: 'legacy/allAppointmentTypesWithActiveConfigurations',
      telehealthPractice: 'legacy/telehealthPractice',
      headerH: 'ui/headerHeight',
      initCount: 'ui/initCount',
      filters: 'ui/filters'
    }),
    calendarsKeys () {
      return Object.keys(this.calendars || {})
    },
    calendarsValues () {
      return Object.values(this.calendars || {})
    },
    calendarsToShowKeys () {
      return this.calendarsKeys.filter(key => Object.prototype.hasOwnProperty.call(this.calendarsToShow, key))
    },
    calendarNavClasses () {
      const list = []

      if (!this.showFilterRow && !this.isMobile) {
        list.push('pt-4 mb-4')
      }
      return list
    },
    showCalendars () {
      return this.calendarsKeys.length > 0
    },
    filteredBlocks () {
      const visitTypeFilterValueMap = {}
      visitTypeFilterValueMap[VISIT_TYPE_IN_PERSON_VALUE] = AVAILABILITY_TYPE_IN_OFFICE_ONLY
      visitTypeFilterValueMap[VISIT_TYPE_VIRTUAL_VISIT_VALUE] = AVAILABILITY_TYPE_VIRTUAL_ONLY

      const visitTypeFilterValue = visitTypeFilterValueMap[this.filters.visitTypeFilter.value]

      const blocks = {}
      this.calendarsKeys.forEach(calendarIndex => {
        const calendar = this.calendars[calendarIndex]
        blocks[calendarIndex] = { timeBlocks: {} }
        for (const [dayIndex, timeBlocksArr] of Object.entries(calendar.timeBlocks)) {
          if (this.fieldSettings(calendarIndex).visitType?.hide && (this.filters.visitTypeFilter.value === VISIT_TYPE_VIRTUAL_VISIT_VALUE)) {
            // telehealth not offered and was selected
            blocks[calendarIndex].timeBlocks[dayIndex] = []
          } else {
            blocks[calendarIndex].timeBlocks[dayIndex] = timeBlocksArr.filter((timeBlock) => {
              const availabilityTypeValid = (typeof timeBlock.availabilityType === 'undefined') || (timeBlock.availabilityType === visitTypeFilterValue) || (timeBlock.availabilityType === AVAILABILITY_TYPE_IN_OFFICE_OR_VIRTUAL)
              const externalReasonIsValid = timeBlock.externalReasonIds.length > 0 ? timeBlock.externalReasonIds.some(id => (id || '').toString() === (this.selectedExternalReasonId || '').toString()) : true
              return availabilityTypeValid && externalReasonIsValid
            })
          }
        }
      })
      return blocks
    },
    someTimeSlotsHasExternalReasonId () {
      return Object.values(this.getCalendarsObjByIndexesArray(this.calendarIndexesWithValidScheduleCheck) || {}).some(calendar => {
        return Object.values(calendar?.timeBlocks ?? {}).some(day => day.some(timeBlock => (timeBlock?.externalReasonIds ?? []).length > 0))
      })
    },
    locationOptions () {
      return this.allLocations.filter(location => {
        return this.filteredLocationsList.has(location.value)
      }).sort((l1, l2) => {
        return l1.sortId - l2.sortId
      })
    },
    providerOptions () {
      return this.allProviders.filter(provider => {
        return this.filteredProvidersList.has(provider.value)
      }).sort((p1, p2) => {
        return p1.sortId - p2.sortId
      })
    },
    allLocations () {
      return this.getLocations(this.calendars)
    },
    allProviders () {
      return this.getProviders(this.calendars)
    },
    filterableCalendarIndexes () {
      return this.appointmentTypesEnabledAndConfigured
        ? this.getCalendarIndexesListByScheduleIdsList(this.scheduleIdsListWIthEnabledApptTypes)
        : this.calendarsKeys
    },
    allFilterableProviders () {
      return this.getProviders(this.getCalendarsObjByIndexesArray([
        ...this.filterableCalendarIndexes
      ]))
    },
    calendarTypes () {
      return this.calendarsValues.map(calendar => calendar?.type || CALENDAR_TYPES.PRACTICE_TYPE)
    },
    /*
     returns object of calendars validation checks per filter, based on this data we make decision show calendar or now
     example
      - locationIdFilter set to 1234, no other filters set
      - 2 calendars
        {
          calendarOne: {
            locations: [1234]
          },
          calendarTwo: {
            locations: [2345]
          }
        }
     result
      {
        calendarOne: {
          locationValid: true,
          providerValid: true,
          scheduleValid: true,
          insuranceValid: true
        },
        calendarTwo: {
          locationValid: false, // false because this calendar location id is not equal to filter value
          providerValid: true,
          scheduleValid: true,
          insuranceValid: true
        }
      }
     */
    calendarsChecksForFilters () {
      return this.calendarsKeys.reduce((acc, key) => {
        const calendar = this.calendars[key]
        const locationValid = () => {
          if (this.filterIsFilled('locationIdFilter')) {
            return this.calendarIndexToLocationAndProviderMap[key]?.displayed?.filterableLocationIds.includes(this.filters.locationIdFilter.value)
          }
          return true
        }
        const providerValid = () => {
          if (this.filterIsFilled('providerIdFilter')) {
            return this.calendarIndexToLocationAndProviderMap[key]?.displayed?.filterableProviderIds.includes(this.filters.providerIdFilter.value)
          }
          return true
        }
        const scheduleValid = () => {
          if (!this.appointmentTypesEnabled || !this.filterIsFilled('appointmentReasonFilter')) {
            return true
          }
          return this.isSpecificAppointmentTypeHasConfigForCalendar(this.filters.appointmentReasonFilter.value, calendar.scheduleId)
        }

        const insuranceValid = () => {
          if (this.filterIsFilled('insuranceProviderIdFilter')) {
            if ([NO_INSURANCE_OPTION_ID, OTHER_INSURANCE_OPTION_ID].includes(this.filters.insuranceProviderIdFilter.value)) {
              return calendar.settings?.allowNoInsAnswer
            }
            return (this.insurances[this.filters.insuranceProviderIdFilter.value]?.calendarIndexes || []).includes(key)
          }
          return true
        }

        acc[key] = {
          locationValid: locationValid(),
          providerValid: providerValid(),
          scheduleValid: scheduleValid(),
          insuranceValid: insuranceValid()
        }
        return acc
      }, {})
    },
    getCalendarIndexesListByScheduleIdsList () {
      return (scheduleIds) => {
        return this.calendarsKeys.reduce((acc, key) => {
          if ((scheduleIds ?? []).includes(this.calendars[key].scheduleId)) {
            acc.push(key)
          }
          return acc
        }, [])
      }
    },
    getCalendarsObjByIndexesArray () {
      return (indexes = []) => {
        const calendarsObj = {}
        Array.isArray(indexes) && indexes.forEach((index) => {
          this.calendars[index] && (calendarsObj[index] = this.calendars[index])
        })
        return calendarsObj
      }
    },
    calendarIndexesWithValidScheduleCheck () {
      return this.calendarsKeys.filter(key => {
        const mainCalendarInfo = this.calendarsChecksForFilters[key]
        return mainCalendarInfo.scheduleValid
      })
    },
    calendarIndexesUsableForAppointmentTypesOptions () {
      return this.calendarsKeys.filter(key => {
        const mainCalendarInfo = this.calendarsChecksForFilters[key]
        return mainCalendarInfo.locationValid && mainCalendarInfo.providerValid
      })
    },
    calendarIndexesUsableForLocationOptions () {
      return this.calendarsKeys.filter(key => {
        const mainCalendarInfo = this.calendarsChecksForFilters[key]
        return mainCalendarInfo.scheduleValid && mainCalendarInfo.providerValid
      })
    },
    calendarIndexesUsableForProviderOptions () {
      return this.calendarsKeys.filter(key => {
        const mainCalendarInfo = this.calendarsChecksForFilters[key]
        return mainCalendarInfo.scheduleValid && mainCalendarInfo.locationValid
      })
    },
    calendarsToShow () {
      let filteredCalendars = this.getCalendarsObjByIndexesArray(this.calendarIndexesUsableForAppointmentTypesOptions)
      if (this.filters.locationIdFilter.value || this.filters.providerIdFilter.value || this.filters.visitTypeFilter.value) {
        filteredCalendars = this.calendarIndexesUsableForAppointmentTypesOptions
          .filter((key) => {
            if (!this.showTimeSelectionBlocks) {
              return false
            }
            const mainCalendarInfo = this.calendarsChecksForFilters[key]
            return mainCalendarInfo.scheduleValid
          }).sort((keyA, keyB) => {
            return this.calendars[keyA].sortingInfo.calendar - this.calendars[keyB].sortingInfo.calendar
          })
          .reduce((obj, key) => {
            if (this.calendars[key]) {
              obj[key] = this.calendars[key]
            }
            return obj
          }, {})
      }
      return filteredCalendars
    },
    daySlots () {
      return [...new Array(this.daysToShow)].map((day, index) => `calendar-nav-day-${index}`)
    },
    daysToShow () {
      return this.$ppUi.isMobile ? 1 : 5
    },
    daysToNavigate () {
      return this.$ppUi.isMobile ? 1 : 5
    },
    scrollableHorizontal () {
      return !!this.$ppUi.isMobile
    },
    nextScreenStartDay () {
      const nextScreenStart = new Date(this.startDate)
      nextScreenStart.setHours(0, 0, 0, 0)
      nextScreenStart.setDate(nextScreenStart.getDate() + this.daysToShow)
      return nextScreenStart
    },
    endDate () {
      const theEndDate = new Date(this.startDate)
      theEndDate.setDate(theEndDate.getDate() + this.daysToShow)
      return theEndDate
    },
    maxDate () {
      let maxDate = new Date(Date.now())
      this.calendarsToShowKeys
        .map((calendarId) => {
          maxDate = this.maxDateInCalendar(calendarId, maxDate)
        })
      return maxDate
    },
    futureDaysCount () {
      const dayCount = Math.ceil((this.maxDate.getTime() - this.nextScreenStartDay.getTime()) / (1000 * 3600 * 24))
      return dayCount < 1 ? 0 : dayCount
    },
    disableBackButton () {
      return (this.startDate - new Date(Date.now())) <= 0
    },
    disableForwardButton () {
      return (this.maxDate - this.endDate) < 0
    },
    legacyReasonsOptions () {
      const options = []
      if (!this.reasons) {
        return options
      }
      for (const reason of Object.values(this.reasons)) {
        let showOptionInList = validLegacyReasonType(reason.reasonType, {
          newPatientFilter: this.filters.newPatientFilter.value
        })

        const validLocationId = this.filterIsFilled('locationIdFilter') && reason.locationId ? +this.filters.locationIdFilter.value === +reason.locationId : true
        const validProviderId = this.filterIsFilled('providerIdFilter') && reason.doctorId ? +this.filters.providerIdFilter.value === +reason.doctorId : true
        showOptionInList = showOptionInList && validLocationId && validProviderId

        if (showOptionInList) {
          options.push({ text: reason.name, value: reason.id })
        }
      }
      return options
    },
    scheduleIdsListWIthEnabledApptTypes () {
      const list = new Set()
      this.allAppointmentTypesWithActiveConfigurations.forEach((apptType) => {
        apptType.configurations.forEach(config => {
          if (config.active && this.allCalendarsScheduleList.includes(config.scheduleId)) {
            list.add(config.scheduleId)
          }
        })
      })
      return [...list]
    },
    appointmentTypesOptions () {
      const options = []
      const providerOrLocationAlreadySelected = this.filterIsFilled('locationIdFilter') || this.filterIsFilled('providerIdFilter')
      const scheduleList = providerOrLocationAlreadySelected ? this.scheduleIdsListForCalendarsThatUsableForAppointmentTypesFiltering : this.allCalendarsScheduleList
      this.configuredAppointmentTypesByScheduleIdsList(scheduleList).forEach((apptType) => {
        const showOptionInList = validAppointmentTypeByPatientType(apptType.configurations, {
          newPatientFilter: this.filters.newPatientFilter.value
        })

        if (showOptionInList) {
          options.push({ text: apptType.displayName, value: apptType.appointmentTypeId })
        }
      })
      return options
    },
    reasonOptions () {
      return this.appointmentTypesEnabled ? this.appointmentTypesOptions : this.legacyReasonsOptions
    },
    insuranceOptions () {
      const options = []
      if (!this.insurances) {
        return options
      }

      for (const insurance of Object.values(this.insurances)) {
        let valid = true
        if (this.filterIsFilled('providerIdFilter')) {
          // if no info about supported providers, check supported calendars
          valid = insurance.providerIds.length > 0 ? insurance.providerIds.includes(this.filters.providerIdFilter.value) : this.calendarsToShowKeys.some(calendarIndex => insurance.calendarIndexes.includes(calendarIndex))
        }
        valid && options.push({ text: insurance.provider, value: insurance.insuranceProviderId || insurance.id })
      }
      options.sort((a, b) => {
        if (a.text === b.text) { return 0 }
        return a.text > b.text ? 1 : -1
      })

      if (this.calendarsToShowKeys.some((calendarKey) => this.calendars[calendarKey].settings?.allowNoInsAnswer)) {
        options.unshift({ text: 'No Insurance', value: NO_INSURANCE_OPTION_ID }, { text: 'Other Insurance', value: OTHER_INSURANCE_OPTION_ID })
      }

      return options
    },
    showPatientTypeToggle () {
      return this.isMobile
    },
    showVisitTypeFilterInFilterRow () {
      return this.showFilter('visitTypeFilter') && this.isMobile
    },
    showVisitTypeFilterNearCalendar () {
      return this.showFilter('visitTypeFilter') && !this.showVisitTypeFilterInFilterRow
    },
    isVirtualVisitFilterEnabled () {
      return this.filters.visitTypeFilter.value === VISIT_TYPE_VIRTUAL_VISIT_VALUE
    },
    appointmentTypesEnabledAndConfigured () {
      return (this.appointmentTypesEnabled && this.allAppointmentTypesWithActiveConfigurations.length > 0)
    },
    /*  filterRow could be shown only if loading is finished plus one of next conditions is true
          * and practice not from group A (has screening questions or reason complexity)
          * appointmentTypesEnabled and at least one apptType configured for one or more schedules(calendars)
     */
    showFilterRow () {
      return !this.isLoading &&
        (
          this.requireSearchBeforeTimeShowing ||
          (this.getPracticeGroup !== CALENDAR_GROUPS.A) ||
          this.showVisitTypeFilterInFilterRow ||
          this.appointmentTypesEnabledAndConfigured ||
          this.someTimeSlotsHasExternalReasonId
        )
    },
    showHeaderContent () {
      return this.showCalendars && !this.isLoading
    },
    requireSearchBeforeTimeShowing () {
      return this.getPracticeGroup === CALENDAR_GROUPS.C && this.practiceIsTwoWayIntegrated && (this.calendarsKeys.length > 1)
    },
    showTimeSelectionBlocks () {
      return this.requireSearchBeforeTimeShowing ? this.showFilteredResults : true
    },
    filterRowClasses () {
      const list = []
      list.push(this.isMobile ? 'mt-0 pt-0 px-3 pb-3' : 'pt-0')
      list.push(!this.isMobile && this.requireSearchBeforeTimeShowing ? 'mt-4' : '')
      list.push(!this.isMobile && !this.requireSearchBeforeTimeShowing ? 'mt-n2' : '')
      if (this.requireSearchBeforeTimeShowing) {
        list.push('filters-container--with-search')
      }

      return list
    },
    showedFilters () {
      if (!this.showFilterRow) {
        return []
      }
      return Object.keys(this.filters).filter(name => this.showFilter(name))
    },
    allShowedFiltersFilled () {
      return !(this.showedFilters.length > 0) || this.showedFilters.every(name => this.filterIsFilled(name))
    },
    practiceIsTwoWayIntegrated () {
      return this.calendarsValues.some((calendar) => TWO_WAY_EXTERNAL_CALENDAR_SOURCES.includes(calendar?.settings?.externalCalendarSource || ''))
    },
    colClasses () {
      const list = []
      if (this.isMobile) {
        list.push('px-0')
      } else if (this.requireSearchBeforeTimeShowing) {
        list.push('mb-0')
      } else {
        list.push('mx-0')
      }
      return list
    },
    calendarHasNoAvailabilityInCurrentDays () {
      return (calendarIndex) => {
        return !this.daySlots.some((daySlot, dayIndex) => {
          return (this.filteredBlocks[calendarIndex].timeBlocks[this.getDateByDaysFromStartDate(dayIndex)]?.length > 0)
        })
      }
    },
    nearestAvailabilityInFuture  () {
      return (calendarIndex) => {
        let dayIndex = 0
        let nearestAvailableDate = null
        for (dayIndex; dayIndex <= this.futureDaysCount; dayIndex++) {
          if (nearestAvailableDate) {
            continue
          }
          const dayIndexFromStart = this.daysToShow + dayIndex
          const date = this.getFutureDateByCountFromStartDate(dayIndexFromStart)
          if (this.filteredBlocks[calendarIndex].timeBlocks[this.getDateByDaysFromStartDate(dayIndexFromStart)]?.length > 0) {
            nearestAvailableDate = {
              date: date,
              dateForNavigation: date,
              text: this.getDateByDaysFromStartDate(dayIndexFromStart, {
                weekday: 'short',
                day: 'numeric',
                month: 'short'
              })
            }
          }
        }
        return nearestAvailableDate
      }
    },
    calendarHasAvailabilityInFuture  () {
      return (calendarIndex) => {
        return !!this.nearestAvailabilityInFuture(calendarIndex)
      }
    },
    showNextAvailableButton () {
      return (calendarIndex) => {
        return this.calendarHasNoAvailabilityInCurrentDays(calendarIndex) && this.calendarHasAvailabilityInFuture(calendarIndex)
      }
    },
    calendarHasNoAvailability () {
      return (calendarIndex) => {
        return this.calendarHasNoAvailabilityInCurrentDays(calendarIndex) && !this.calendarHasAvailabilityInFuture(calendarIndex)
      }
    },
    showCallOfficeBlock () {
      return (calendarIndex) => {
        const res = this.showScreening(calendarIndex) || this.calendarHasNoAvailability(calendarIndex) || !this.calendarSupportsSelectedInsurance(calendarIndex)
        return res
      }
    },
    scheduleIdsListForCalendarsThatUsableForAppointmentTypesFiltering () {
      return this.getListOfCalendarsScheduleIds(Object.values(this.getCalendarsObjByIndexesArray(this.calendarIndexesUsableForAppointmentTypesOptions)))
    },
    showedCalendarsScheduleIdsList () {
      return this.getListOfCalendarsScheduleIds(Object.values(this.calendarsToShow))
    },
    allCalendarsScheduleList () {
      return this.getListOfCalendarsScheduleIds(this.calendarsValues)
    },
    getAppoinmentTypeById () {
      return (apptTypeId) => {
        return this.allAppointmentTypesWithActiveConfigurations.find(type => type.appointmentTypeId === apptTypeId) || null
      }
    },
    isSpecificAppointmentTypeHasConfigForCalendar () {
      return (apptTypeId, scheduleId) => {
        const appointmentType = this.getAppoinmentTypeById(apptTypeId)
        if (!appointmentType || !apptTypeId || !scheduleId) {
          return false
        }
        return appointmentType.configurations.some(config => config.scheduleId === scheduleId && config.active)
      }
    },
    legacyReasonForSpecificAppointmentType () {
      return (apptTypeUuid) => {
        if (!this.reasons || !apptTypeUuid) {
          return null
        }
        const legacyReasonForThisApptType = Object.values(this.reasons).find((reason) => reason.appointmentTypeUuid === apptTypeUuid)
        return legacyReasonForThisApptType ?? null
      }
    },
    legacyReasonById () {
      return (legacyReasonId) => {
        const reason = Object.values(this.reasons).find(reason => reason.id === legacyReasonId)
        return reason || null
      }
    },
    showLoadingError () {
      return !this.isLoading && !this.showCalendars
    },
    filtersInfo () {
      return [
        {
          name: 'newPatientFilter',
          showFilter: this.showFilter('newPatientFilter'),
          options: this.filters.newPatientFilter.expand.toggleItems,
          disabled: false,
          errors: this.getFilterErrors('newPatientFilter'),
          filterComponent: this.showPatientTypeToggle ? 'pp-input-toggle-group' : 'pp-input-checkbox',
          label: '',
          attrs: {
            'data-cy-id': this.showPatientTypeToggle ? 'patient-type-toggle' : 'form-new-patient',
            label: this.showPatientTypeToggle ? '' : 'I\'m a new patient at this practice',
            mandatory: this.showPatientTypeToggle,
            small: !this.showPatientTypeToggle,
            class: [
              this.showPatientTypeToggle ? 'patient-type-toggle' : (this.requireSearchBeforeTimeShowing ? '' : 'mt-4')
            ],
            'false-value': false,
            name: 'newPatient'
          }
        },
        {
          name: 'visitTypeFilter',
          showFilter: this.showVisitTypeFilterInFilterRow,
          options: this.filters.visitTypeFilter.expand.options,
          disabled: this.disableFilter('visitTypeFilter'),
          errors: this.getFilterErrors('visitTypeFilter'),
          filterComponent: 'pp-input-select',
          label: 'Visit type',
          attrs: {
            'data-cy-id': 'visit-type-options',
            placeholder: 'Visit type'
          }
        },
        {
          name: 'appointmentReasonFilter',
          showFilter: this.showFilter('appointmentReasonFilter'),
          options: this.reasonOptions,
          disabled: this.disableFilter('appointmentReasonFilter'),
          errors: this.getFilterErrors('appointmentReasonFilter'),
          filterComponent: 'pp-input-select',
          label: 'Reason for visit',
          menuContentClass: 'reason-content-list',
          attrs: {
            autocomplete: 'off',
            'data-cy-id': 'reason-options',
            placeholder: 'Reason for visit'
          }
        },
        {
          name: 'locationIdFilter',
          showFilter: this.showFilter('locationIdFilter'),
          options: this.locationOptions,
          disabled: this.disableFilter('locationIdFilter'),
          errors: this.getFilterErrors('locationIdFilter'),
          filterComponent: 'pp-input-select',
          label: 'Location',
          menuContentClass: 'location-content-list',
          attrs: {
            autocomplete: 'off',
            'data-cy-id': 'location-options',
            placeholder: 'Select location',
            clearable: true
          }
        },
        {
          name: 'providerIdFilter',
          showFilter: this.showFilter('providerIdFilter'),
          options: this.providerOptions,
          disabled: this.disableFilter('providerIdFilter'),
          errors: this.getFilterErrors('providerIdFilter'),
          filterComponent: 'pp-input-select',
          label: 'Provider',
          menuContentClass: 'provider-content-list',
          attrs: {
            'data-cy-id': 'provider-options',
            placeholder: 'Select provider',
            clearable: true,
            autocomplete: 'off'
          }
        },
        {
          name: 'insuranceProviderIdFilter',
          showFilter: this.showFilter('insuranceProviderIdFilter'),
          options: this.insuranceOptions,
          disabled: this.disableFilter('insuranceProviderIdFilter'),
          errors: this.getFilterErrors('insuranceProviderIdFilter'),
          filterComponent: 'pp-input-select',
          label: 'Insurance',
          attrs: {
            autocomplete: 'off',
            'data-cy-id': 'insurance-options',
            placeholder: 'Insurance'
          }
        }
      ]
    },
    selectedLegacyReason () {
      return this[this.appointmentTypesEnabled ? 'legacyReasonForSpecificAppointmentType' : 'legacyReasonById'](this.filters.appointmentReasonFilter.value)
    },
    selectedExternalReasonId () {
      return this.selectedLegacyReason ? this.selectedLegacyReason.externalReasonId : null
    },
    filteredLocationsList () {
      const locationIdsList = new Set()
      this.calendarIndexesUsableForLocationOptions.forEach(calendarIndex => {
        (this.calendarIndexToLocationAndProviderMap[calendarIndex]?.displayed?.filterableLocationIds ?? []).forEach(lId => (locationIdsList.add(lId)))
      })
      return locationIdsList
    },
    // list of unique providerIds that could be used as providerFilter options
    filteredProvidersList () {
      const providerIdsList = new Set()
      this.calendarIndexesUsableForProviderOptions.forEach(calendarIndex => {
        (this.calendarIndexToLocationAndProviderMap[calendarIndex]?.displayed?.filterableProviderIds ?? []).forEach(pId => (providerIdsList.add(pId)))
      })
      return providerIdsList
    },
    calendarSupportsSelectedInsurance () {
      return (calendarIndex) => {
        return this.calendarsChecksForFilters[calendarIndex].insuranceValid
      }
    },
    providersThatSupportsSelectedInsurance () {
      if (this.filterIsFilled('insuranceProviderIdFilter')) {
        let providerIds = []
        // no/other insurance options
        if ([NO_INSURANCE_OPTION_ID, OTHER_INSURANCE_OPTION_ID].includes(this.filters.insuranceProviderIdFilter.value)) {
          providerIds = [...this.calendarsKeys.reduce((acc, key) => {
            const calendar = this.calendars[key]
            if (calendar.settings?.allowNoInsAnswer) {
              this.calendarIndexToLocationAndProviderMap[key].displayed.filterableProviderIds.forEach(id => {
                acc.add(id)
              })
            }
            return acc
          }, new Set())]
        } else {
          // normal insurance provider option
          for (const id in this.insurances) {
            if (this.filters.insuranceProviderIdFilter.value === this.insurances[id].insuranceProviderId) {
              providerIds = this.insurances[id]?.providerIds ?? []
            }
          }
        }
        return providerIds
      } else {
        return []
      }
    }
  },
  watch: {
    initCount (newVal, oldVal) {
      if (newVal > oldVal) {
        this.sendLoadedEvents()
      }
    }
  },
  created () {
    let track = false
    if (this.$route.params.error) {
      this.setNotification({ message: this.$route.params.error.message, duration: 6000 })
    } else if (![REFERRER_BACK_BUTTON, REFERRER_CLOSE_BUTTON].includes(this.$route.params.referrer)) {
      track = !(this.initCount > 0) // send 'firts load' only once
      this.sendLoadedEvents()
    }
    this.clearCalendars()
    this.getPracticeCalendarInfo({ practiceId: this.$practiceId, track })
      .then(() => {
        const promises = []
        this.calendarsKeys.forEach(calId => {
          promises.push(this.getOneCalendarInfo({ providerId: this.calendars[calId].providerId, locationId: this.calendars[calId].locationId }))
        })
        Promise.all(promises).then(() => {
          this.isLoading = false
          this.clearRequestQueue()
          this.clearSavedCalendarIdsList()
          this.resetInsurance()
          this.setPreselectedFilters()
          // if widget opened via dedicated cloudfront url, default patientPop apiKey need to be used instead of practice mapApiKey
          if (this.$urlBasedFrame) {
            this.setGoogleMapsApiKey(null)
          } else {
            this.loadGoogleMapsApiKey({ practiceId: this.$practiceId })
          }
        })
      })
      .catch((e) => {
        this.trackEvent({
          action: 'appointment_form_error'
        })
        // don't send error if practice don't have calendars or have just one calendar and no availability
        if ((e?.status ?? 'network') !== NOCALENDAR_STATUS) {
          console.error(e)
        }
        this.errorMessage = e.message
        this.isLoading = false
      })
  },
  methods: {
    ...mapMutations({
      clearCalendars: `legacy/${types.CLEAR_CALENDARS}`,
      clearRequestQueue: `legacy/${types.CLEAR_REQUEST_QUEUE}`,
      clearSavedCalendarIdsList: `legacy/${types.CLEAR_SAVED_CALENDARS_IDS_LIST}`,
      setGoogleMapsApiKey: `legacy/${types.SET_GOOGLE_MAPS_API_KEY}`,
      setFilterRowShownState: 'ui/SET_FILTER_ROW_SHOWN_STATE'
    }),
    ...mapActions({
      getPracticeCalendarInfo: 'legacy/getPracticeCalendarInfo',
      getOneCalendarInfo: 'legacy/getCalendarInfo',
      loadGoogleMapsApiKey: 'legacy/loadGoogleMapsApiKey',
      setFormData: 'formData/setFormData',
      setFilters: 'ui/setFilters',
      setNotification: 'ui/setSnackMessage'
    }),
    sendLoadedEvents () {
      setTimeout(() => {
        this.trackEvent({
          action: 'appointment_form_load'
        })
        datadogRum.addAction('loaded', {
          practice: {
            id: this.$practiceId
          }
        })
      }, 500)
    },
    navigateToNextAvailable (date) {
      this.trackEvent({
        action: 'next_available_btn_clicked',
        label: 'Navigated to next available'
      })
      this.setStartDate(date)
    },
    setStartDate (date) {
      date = new Date(date)
      if (!date) {
        return
      }
      const dateToSet = date > this.minDate ? date : new Date(this.minDate)
      this.startDate = dateToSet
    },
    maxDateInCalendar (calendarId, maxDate = new Date(Date.now())) {
      if (this.calendars[calendarId]?.timeBlocks && Object.values(this.calendars[calendarId].timeBlocks).length > 0) {
        const latestTimeBlock = Object.values(this.calendars[calendarId].timeBlocks).slice(-1)[0].slice(-1)[0]
        const latestTimeBlockDate = new Date(`${latestTimeBlock.date}T${latestTimeBlock.startTime}`)
        if (latestTimeBlockDate > maxDate) {
          maxDate = latestTimeBlockDate
        }
      }
      return maxDate
    },
    getFutureDateByCountFromStartDate (daysCount) {
      if (+daysCount < 0 || (typeof +daysCount !== 'number' && !daysCount) || isNaN(+daysCount)) {
        return null
      }
      const futureDate = new Date(this.startDate)
      futureDate.setHours(0, 0, 0, 0)
      futureDate.setDate(futureDate.getDate() + +daysCount)
      return futureDate
    },
    showFilter (filterName) {
      switch (filterName) {
        case 'newPatientFilter':
          return true
        case 'visitTypeFilter':
          // show if telehealth is enabled for practice, unless it's telehealth only
          return !this.telehealthPractice &&
            this.calendarsKeys.some(calendarIndex => {
              return (!this.fieldSettings(calendarIndex)?.visitType?.hide || ScreeningSettings.hasTelehealthEnabled(this.screeningSettings(calendarIndex)))
            })
        case 'locationIdFilter':
          // show filter anyway if filter item selected
          return (this.locationOptions.length >= 0 && this.allLocations.length > 1 && !this.telehealthPractice) || this.filterIsFilled('locationIdFilter')
        case 'providerIdFilter':
          // show filter anyway if filter item selected
          return (this.providerOptions.length >= 0 && this.allFilterableProviders.length > 1) || this.filterIsFilled('providerIdFilter')
        case 'appointmentReasonFilter':
          // show this filter for 2way integrated practices only if search is required or appointmentTypesEnabled
          return this.reasonOptions.length > 0 && (this.requireSearchBeforeTimeShowing ? true : (!this.practiceIsTwoWayIntegrated || this.appointmentTypesEnabled || this.someTimeSlotsHasExternalReasonId))
        case 'insuranceProviderIdFilter':
          // if practice has screening questions about insurance
          return (this.insuranceOptions.length > 0) && this.calendarsKeys.some(calendarIndex => {
            return screeningQuestions.hasInsuranceSetting(this.screeningSettings(calendarIndex))
          })
        default:
          return false
      }
    },
    disableFilter (filterName) {
      if (['providerIdFilter'].includes(filterName)) {
        return this.providerOptions.length === 0
      }
      if (['locationIdFilter'].includes(filterName)) {
        return this.locationOptions.length === 0
      }
      if (!this.requireSearchBeforeTimeShowing) {
        return false
      }
      // reqson filter could be disabled only if search required (2-way integrated practice)
      if (['appointmentReasonFilter'].includes(filterName)) {
        const locationFilled = (this.showFilter('locationIdFilter') ? this.filterIsFilled('locationIdFilter') : true)
        const providerFilled = (this.showFilter('providerIdFilter') ? this.filterIsFilled('providerIdFilter') : true)
        return !locationFilled || !providerFilled
      }
      // all other filters can't be disabled
      return false
    },
    filterChanged ({ filterName, value, trackFilter = false } = { filterName: '', value: null, trackFilter: false }) {
      this.showFilteredResults = false
      this.filters[filterName] && (this.filters[filterName].value = value)
      this.resetDependedFilters(filterName)
      trackFilter && this.trackFilterByName(filterName)
    },
    trackFilterByName (filterName) {
      switch (filterName) {
        case 'visitTypeFilter':
          this.trackFilter('visit-type')
          break
        case 'locationIdFilter':
          this.trackFilter('location')
          break
        case 'providerIdFilter':
          this.trackFilter('provider')
          break
        case 'appointmentReasonFilter':
          this.trackFilter('reason')
          break
        case 'insuranceProviderIdFilter':
          this.trackFilter('insuranceProviderId')
          break
      }
    },
    resetDependedFilters (filterName) {
      if (!Object.prototype.hasOwnProperty.call(this.filters, filterName)) {
        return false
      }
      const mainFiltersList = ['locationIdFilter', 'providerIdFilter', 'newPatientFilter']
      if (mainFiltersList.includes(filterName)) {
        this.resetReason()
      }
      // insurance depends on provider selection only
      if (['providerIdFilter'].includes(filterName)) {
        this.resetInsurance()
      }
    },
    resetReason () {
      let valid = false
      if (this.reasonOptions.length > 0) {
        valid = this.reasonOptions.some(reasonOption => {
          return (reasonOption.value === this.filters.appointmentReasonFilter.value)
        })
      }
      if (!valid) {
        let clearedValue = null
        if (!this.requireSearchBeforeTimeShowing && this.showFilterRow && this.reasonOptions.length > 0) {
          clearedValue = this.reasonOptions[0].value
        }
        this.$set(this.filters.appointmentReasonFilter, 'value', clearedValue)
      }
    },
    resetInsurance () {
      let valid = false
      if (this.insuranceOptions.length > 0) {
        valid = this.insuranceOptions.some(insuranceOption => {
          return (insuranceOption.value === this.filters.insuranceProviderIdFilter.value)
        })
      }
      if (!valid && this.showFilter('insuranceProviderIdFilter')) {
        const clearedValue = this.requireSearchBeforeTimeShowing ? null : (this.insuranceOptions.length > 0 ? this.insuranceOptions[0].value : null)
        this.$set(this.filters.insuranceProviderIdFilter, 'value', clearedValue)
      }
    },
    getFilterErrors (filterName) {
      const errList = []
      if (!this.showFilterErrors || !this.requireSearchBeforeTimeShowing) {
        return errList
      }

      !this.filterIsFilled(filterName) && !this.disableFilter(filterName) && (errList.push('Required'))
      return errList
    },
    clickMore (calendarIndex) {
      this.showAllTimes.push(calendarIndex)
    },
    showMore (calendarIndex) {
      return (!this.$ppUi.isMobile && Object.values(this.calendarsToShow).length === 1) ||
        this.showAllTimes.includes(calendarIndex)
    },
    getDateByDaysFromStartDate (days, options = {
      day: 'numeric',
      month: 'short',
      year: '2-digit'
    }) {
      const result = this.getFutureDateByCountFromStartDate(days)
      return result ? result.toLocaleDateString(
        'en-us',
        options
      ) : ''
    },
    updateCalendarNav (calNav) {
      this.setStartDate(calNav.start)
      this.showAllTimes = []
      this.trackEvent({
        action: 'calendar_nav',
        label: calNav.direction
      })
    },
    setValidProviderAndLocation (calendarIndex, payload) {
      const calendar = this.calendars[calendarIndex]

      const validateLocation = () => {
        if (payload.locationId && payload.locationId !== calendar.displayInfo.displayLocations.current.id) {
          console.warn('location ID for location calendar was set to an incorrect ID')
        }
        if (calendar.displayInfo.displayLocations.current) {
          payload.locationId = calendar.displayInfo.displayLocations.current.id
        }
        payload.locationId = calendar.displayInfo.displayLocations.list.length > 0 ? calendar.displayInfo.displayLocations.list[0].id : null
      }
      const validateProvider = () => {
        const correctProviderId = calendar.settings.communication.showAsOtherCalendar
          ? calendar.originalProviders.current.id : calendar.displayInfo.displayProviders.current.id
        if (payload.doctorId && payload.doctorId !== correctProviderId) {
          console.warn('provider ID for provider calendar was set to an incorrect ID')
        }
        payload.showedDoctorId = calendar.displayInfo.displayProviders.current.id
        payload.doctorId = correctProviderId
      }
      // Sometimes the user is not given a chance to select a provider/location. so we must fix the selection.
      switch (calendar.type) {
        case CALENDAR_TYPES.LOCATION_TYPE:
          if (calendar.displayInfo.displayProviders.list.length === 1) {
            validateProvider()
          }
          validateLocation()
          break
        case CALENDAR_TYPES.PROVIDER_TYPE:
          validateProvider()
          if (calendar.displayInfo.displayLocations.list.length === 1) {
            validateLocation()
          }
          break
        case CALENDAR_TYPES.PROVIDER_LOCATION_TYPE:
          validateLocation()
          validateProvider()
          break
        case CALENDAR_TYPES.PRACTICE_TYPE:
          if (calendar.displayInfo.displayLocations.list.length === 1) {
            validateLocation()
          }
          if (calendar.displayInfo.displayProviders.list.length === 1) {
            validateProvider()
          }
      }
      if (calendar.settings.skipProviderSelection) {
        payload.doctorId = null
      }
      return payload
    },
    goToNextPage (timeSlot, calendarIndex) {
      this.submitAnalytics('time-slot')
      const payload = this.setValidProviderAndLocation(calendarIndex, {
        ...this.formData,
        ...{
          newPatient: this.filters.newPatientFilter.value,
          isTelehealth: this.filters.visitTypeFilter.value,
          reason: this.filters.appointmentReasonFilter.value,
          insuranceProviderId: this.filters.insuranceProviderIdFilter.value,
          doctorId: this.filters.providerIdFilter.value,
          locationId: this.filters.locationIdFilter.value,
          timeBlock: timeSlot,
          calendarIndex: calendarIndex,
          calendarId: this.calendars[calendarIndex].id,
          scheduleId: this.calendars[calendarIndex]?.scheduleId ?? null,
          calendarBlockId: timeSlot.id
        }
      })
      if (!payload.locationId && !!this.$locationIdFilter && this.calendarIndexToLocationAndProviderMap[calendarIndex]?.displayed?.allLocationIds.includes(+this.$locationIdFilter)) {
        payload.locationId = +this.$locationIdFilter
      }
      if (!payload.doctorId && !!this.$providerIdFilter && this.calendarIndexToLocationAndProviderMap[calendarIndex]?.displayed?.allProviderIds.includes(+this.$providerIdFilter)) {
        payload.doctorId = +this.$providerIdFilter
      }
      const filtersPayload = { ...this.filters }
      this.filtersInfo.forEach(el => {
        filtersPayload[el.name].shownOnStepA = el.showFilter && this.showFilterRow
      })
      const promises = [
        this.setFormData(payload),
        this.setFilters(filtersPayload),
        this.setFilterRowShownState(this.showFilterRow)
      ]
      Promise.all(promises).then(() => {
        this.$store.commit(`ui/${uiTypes.SET_STEP_FILLED}`, 'stepA')
        this.$router.replace({ name: 'stepB', query: this.$route.query })
      })
    },
    getLocations (calendars) {
      const locationIds = []
      const uniqueArray = []
      for (const calendarId in calendars) {
        (this.calendarIndexToLocationAndProviderMap[calendarId]?.displayed?.filterableLocationsList ?? []).forEach(l => {
          if (!locationIds.includes(l.id)) {
            locationIds.push(l.id)
            uniqueArray.push({
              value: l.id,
              providerIds: [
                ...this.calendarIndexToLocationAndProviderMap[calendarId].displayed.filterableProviderIds
              ],
              text: l.internalName,
              sortId: l.sortId
            })
          } else {
            const location = uniqueArray.find((el) => el.value === l.id)
            this.calendarIndexToLocationAndProviderMap[calendarId].displayed.filterableProviderIds.forEach(id => {
              !location.providerIds.includes(id) && location.providerIds.push(id)
            })
          }
        })
      }
      return uniqueArray
    },
    getProviders (calendars) {
      const providerIds = new Set()
      const uniqueArray = []
      for (const calendarId in calendars) {
        (this.calendarIndexToLocationAndProviderMap[calendarId]?.displayed?.filterableProvidersList ?? []).forEach(p => {
          if (!providerIds.has(p.id)) {
            providerIds.add(p.id)
            uniqueArray.push({
              value: p.id,
              locationIds: [
                ...this.calendarIndexToLocationAndProviderMap[calendarId].displayed.filterableLocationIds
              ],
              text: p.text,
              sortId: p.sortId
            })
          } else {
            const provider = uniqueArray.find((el) => el.value === p.id)
            this.calendarIndexToLocationAndProviderMap[calendarId].displayed.filterableLocationIds.forEach(id => {
              !provider.locationIds.includes(id) && provider.locationIds.push(id)
            })
          }
        })
      }
      return uniqueArray
    },
    trackFilter (filterType) {
      this.trackEvent({
        action: 'filter',
        label: filterType
      })
    },
    showScreening (calendarIndex) {
      let reasonId = this.filters.appointmentReasonFilter.value
      if (this.appointmentTypesEnabled) {
        reasonId = this.legacyReasonForSpecificAppointmentType(this.filters.appointmentReasonFilter.value)?.id ?? null
      }
      const dataForScreeningQuestions = {
        ...this.formData,
        newPatient: this.filters.newPatientFilter.value,
        isTelehealth: this.filters.visitTypeFilter.value,
        reason: reasonId,
        insuranceProviderId: this.filters.insuranceProviderIdFilter.value
      }
      // remove null and undefined to avoid error
      for (const k in dataForScreeningQuestions) {
        !['boolean', 'string', 'number'].includes(typeof dataForScreeningQuestions[k]) && (delete dataForScreeningQuestions[k])
        if (Object.prototype.hasOwnProperty.call(FILTER_VALUE_TO_SCREENING_VALUE_MAP, dataForScreeningQuestions[k])) {
          dataForScreeningQuestions[k] = FILTER_VALUE_TO_SCREENING_VALUE_MAP[dataForScreeningQuestions[k]]
        }
      }
      const res = screeningQuestions.apply(this.screeningSettings(calendarIndex), dataForScreeningQuestions)
      return res
    },
    searchCalendars () {
      if (this.allShowedFiltersFilled) {
        this.showFilterErrors = false
        this.showFilteredResults = true
      } else {
        this.showFilterErrors = true
      }
    },
    getListOfCalendarsScheduleIds (calendarsArr = []) {
      if (!Array.isArray(calendarsArr)) {
        return []
      }
      return calendarsArr.reduce((acc, item) => {
        if (item.scheduleId) {
          acc.push(item.scheduleId)
        }
        return acc
      }, [])
    },
    setPreselectedFilters () {
      /* preselect appointment reason filter if
      *    $appointmentTypeIdFilter exist
      *    appointmentReasonFilter is not filled yet
      *    appointmentTypesOptions count is 1+
      *    $appointmentTypeIdFilter exist in appointmentTypesOptions list
       */
      const reasonFilterCouldBePreset = (
        this.$appointmentTypeIdFilter &&
        this.appointmentTypesEnabled &&
        !this.filterIsFilled('appointmentReasonFilter') &&
        this.appointmentTypesOptions.length > 0 &&
        Boolean(this.appointmentTypesOptions.find(apptTypeOption => apptTypeOption.value === this.$appointmentTypeIdFilter))
      )
      if (reasonFilterCouldBePreset) {
        this.filters.appointmentReasonFilter.value = this.$appointmentTypeIdFilter
      }
      /* preselect location filter if
      *    $locationIdFilter exist
      *    locationIdFilter is not filled yet
      *    allLocations count is 2+
      *    $locationIdFilter exist in allLocations list or in locationOptions list if reason filter was preselected
       */
      const locationsList = reasonFilterCouldBePreset ? this.locationOptions : this.allLocations
      if (!!this.$locationIdFilter && !this.filterIsFilled('locationIdFilter') && this.allLocations.length > 1 && !!locationsList.find(el => +el.value === +this.$locationIdFilter)) {
        this.filters.locationIdFilter.value = +this.$locationIdFilter
      }
      const providerList = this.filterIsFilled('locationIdFilter') ? this.providerOptions : this.allProviders
      /* preselect provider filter if
      *    $providerIdFilter exist
      *    providerIdFilter is not filled yet
      *    allProviders count is 2+
      *    $providerIdFilter exist in allLocations list
       */
      if (!!this.$providerIdFilter && !this.filterIsFilled('providerIdFilter') && this.allProviders.length > 1 && !!providerList.find(el => +el.value === +this.$providerIdFilter)) {
        this.filters.providerIdFilter.value = +this.$providerIdFilter
      }
      // reason filter could be reset only if reasonFilter haven't been preset from init params
      !reasonFilterCouldBePreset && this.resetReason()

      // preselect visitType filter with virtual visit if the practice is telehealth only
      this.telehealthPractice && (this.filters.visitTypeFilter.value = VISIT_TYPE_VIRTUAL_VISIT_VALUE)
    }
  }
}
</script>
<style lang="scss" scoped>
.calendar-nav {
  background: $white;
  overflow: hidden;
}

.calendar-row {
  border-bottom: 1px solid $grey-40;
  flex: 0;

  &:last-child {
    border-bottom: none;
  }

  @include mobile() {
    margin: 0 -12px 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.availability-container {
  @include desktop() {
    height: 100%;
    min-height: 180px;
  }
}

.time-blocks {
  @include desktop() {
    background-image: url("data:image/svg+xml;charset=utf8,%3Csvg width='96' height='44' xmlns='http://www.w3.org/2000/svg'%3E%3Cg%3E%3Cline y2='18' x2='55' y1='18' x1='35' stroke-width='1.5' stroke='%2399a1b8' fill='none'/%3E%3C/g%3E%3C/svg%3E");
    background-position: center 10px;
    background-repeat: repeat-y;
    height: 100%;
  }

  &__slot {
    position: relative;

    &--available {
      @include mobile() {
        margin-right: .25em;
      }
    }

    &--booked {
      @include mobile() {
        display: none;
      }

      ::v-deep {
        /* stylelint-disable max-nesting-depth */
        button {
          visibility: hidden;
        }
        /* stylelint-enable max-nesting-depth */
      }

      &:before {
        background: $grey-50;
        content: "";
        height: 1px;
        left: 40%;
        position: absolute;
        top: 50%;
        width: 20%;
        z-index: 800;
      }
    }
  }
}

.pp-scrollable-list {
  ::v-deep {
    .mx-1 {
      @include mobile() {
        /* stylelint-disable declaration-no-important */
        margin-left: 0 !important;
        margin-right: 0 !important;
        /* stylelint-enable declaration-no-important */
      }
    }

    button {
      @include desktop() {
        /* stylelint-disable declaration-no-important */
        padding: 0 !important;
        width: 90% !important;
        /* stylelint-enable declaration-no-important */
      }
    }
  }
}

.pp-avatar-info {
  ::v-deep {
    @include desktop() {
      width: 100% + 833.33/33;
    }
  }
}
</style>
